import { useCallback, useRef, useState } from "react";
import { useSSO, useSignIn, useSignUp } from "@clerk/clerk-expo";

import { explainIncomplete } from "../src/explain-incomplete";
import { useRouter } from "expo-router";
import * as Linking from "expo-linking";
import {
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  View,
} from "react-native";
import { colour, type } from "../src/theme";

/**
 * Email OTP sign-in.
 *
 * Clerk models sign-in and sign-up as separate flows, but from a person's
 * point of view "type your email, type the code" is one thing and they do not
 * know or care whether they have an account. So this tries sign-in first and
 * falls back to sign-up when Clerk says the identifier is unknown -- which is
 * what makes the screen say "Continue" rather than making somebody choose.
 */

type Stage = "email" | "code";

/** Clerk errors arrive as an array of objects, not as an Error. */
function messageFrom(error: unknown, fallback: string): string {
  const errors = (error as { errors?: { longMessage?: string; message?: string }[] })?.errors;
  const first = errors?.[0];
  return first?.longMessage ?? first?.message ?? fallback;
}

function isUnknownIdentifier(error: unknown): boolean {
  const errors = (error as { errors?: { code?: string }[] })?.errors;
  return errors?.some((e) => e.code === "form_identifier_not_found") ?? false;
}

export default function SignInScreen() {
  const router = useRouter();
  const { signIn, setActive: setSignInActive, isLoaded: signInLoaded } = useSignIn();
  const { startSSOFlow } = useSSO();
  const { signUp, setActive: setSignUpActive, isLoaded: signUpLoaded } = useSignUp();

  const [stage, setStage] = useState<Stage>("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Which flow the code belongs to, so verification goes to the right one. */
  const flow = useRef<"sign-in" | "sign-up">("sign-in");

  const ready = signInLoaded && signUpLoaded;

  const sendCode = useCallback(async () => {
    if (!ready || busy) return;
    const address = email.trim();
    if (!address.includes("@")) {
      setError("That does not look like an email address.");
      return;
    }

    setBusy(true);
    setError(null);
    try {
      try {
        const attempt = await signIn.create({ identifier: address });
        const factor = attempt.supportedFirstFactors?.find(
          (f) => f.strategy === "email_code",
        ) as { emailAddressId: string } | undefined;

        if (!factor) {
          // The instance is configured for something else -- a password, a
          // magic link. Saying so beats a spinner that never resolves.
          setError("This account cannot sign in with an email code.");
          return;
        }

        await signIn.prepareFirstFactor({
          strategy: "email_code",
          emailAddressId: factor.emailAddressId,
        });
        flow.current = "sign-in";
      } catch (cause) {
        if (!isUnknownIdentifier(cause)) throw cause;

        // No account yet. From here it is a sign-up, but the screen does not
        // change -- the person typed an email and expects a code either way.
        await signUp.create({ emailAddress: address });
        await signUp.prepareEmailAddressVerification({ strategy: "email_code" });
        flow.current = "sign-up";
      }

      setStage("code");
    } catch (cause) {
      setError(messageFrom(cause, "Could not send a code. Try again."));
    } finally {
      setBusy(false);
    }
  }, [ready, busy, email, signIn, signUp]);

  /**
   * Google, on web only (WI-034).
   *
   * Native is deliberately excluded rather than half-supported. On a device
   * this needs an Expo dev build and three OAuth client IDs, and Apple's
   * Guideline 4.8 then requires Sign in with Apple alongside it -- which needs
   * the paid developer account (WI-H02). A button that silently fails on a
   * phone is worse than no button, so `Platform.OS` gates it.
   */
  const startGoogle = useCallback(() => {
    if (busy) return;
    setBusy(true);
    setError(null);

    startSSOFlow({
      strategy: "oauth_google",
      // Where Clerk sends the browser back to. `createURL` gives the right
      // origin on web and a scheme-based URL on native, so this does not need
      // to know where it is running.
      redirectUrl: Linking.createURL("/", { scheme: "tabletop" }),
    })
      .then(async ({ createdSessionId, setActive: activate }) => {
        if (!createdSessionId || !activate) {
          // Clerk can return without a session when the flow needs more --
          // an unverified email, say. Saying so beats a spinner that stops.
          setError("Google sign-in did not complete. Try the email code instead.");
          return;
        }
        await activate({ session: createdSessionId });
        router.replace("/");
      })
      .catch((cause: unknown) => {
        setError(messageFrom(cause, "Could not sign in with Google."));
      })
      .finally(() => setBusy(false));
  }, [busy, startSSOFlow, router]);

  const verify = useCallback(async () => {
    if (!ready || busy) return;
    const entered = code.trim();
    if (entered.length === 0) return;

    setBusy(true);
    setError(null);
    try {
      if (flow.current === "sign-in") {
        const result = await signIn.attemptFirstFactor({
          strategy: "email_code",
          code: entered,
        });
        if (result.status !== "complete") {
          // Same shape on the sign-in side: the code was fine, something else
          // is outstanding.
          setError(explainIncomplete(result));
          return;
        }
        await setSignInActive({ session: result.createdSessionId });
      } else {
        const result = await signUp.attemptEmailAddressVerification({ code: entered });
        if (result.status !== "complete") {
          setError("That code was not enough to finish signing up.");
          return;
        }
        await setSignUpActive({ session: result.createdSessionId });
      }

      router.replace("/");
    } catch (cause) {
      setError(messageFrom(cause, "That code did not work."));
    } finally {
      setBusy(false);
    }
  }, [ready, busy, code, signIn, signUp, setSignInActive, setSignUpActive, router]);

  return (
    <KeyboardAvoidingView
      style={styles.screen}
      behavior={Platform.OS === "ios" ? "padding" : undefined}
    >
      <Text style={styles.title}>
        {stage === "email" ? "Sign in" : "Check your email"}
      </Text>
      <Text style={styles.blurb}>
        {stage === "email"
          ? "We will email you a code. No password to remember."
          : `We sent a code to ${email.trim()}.`}
      </Text>

      {stage === "email" && Platform.OS === "web" && (
        <>
          <Pressable
            style={[styles.google, busy && styles.buttonDisabled]}
            disabled={busy || !ready}
            onPress={startGoogle}
          >
            <Text style={styles.googleText}>Continue with Google</Text>
          </Pressable>
          <View style={styles.dividerRow}>
            <View style={styles.divider} />
            <Text style={styles.dividerText}>or</Text>
            <View style={styles.divider} />
          </View>
        </>
      )}

      {stage === "email" ? (
        <TextInput
          style={styles.input}
          placeholder="you@example.com"
          placeholderTextColor={colour.inkFaint}
          value={email}
          onChangeText={setEmail}
          autoCapitalize="none"
          autoCorrect={false}
          keyboardType="email-address"
          textContentType="emailAddress"
          returnKeyType="send"
          editable={!busy}
          onSubmitEditing={() => void sendCode()}
        />
      ) : (
        <TextInput
          style={[styles.input, styles.code]}
          placeholder="123456"
          placeholderTextColor={colour.inkFaint}
          value={code}
          onChangeText={setCode}
          keyboardType="number-pad"
          // Lets iOS and Android offer the code straight from the notification.
          textContentType="oneTimeCode"
          autoComplete="one-time-code"
          returnKeyType="done"
          editable={!busy}
          onSubmitEditing={() => void verify()}
        />
      )}

      {/*
        Where Clerk mounts its bot-protection widget.

        The instance has `captcha_enabled: true`. Clerk's prebuilt components
        create this node themselves; a custom flow has to provide it, and
        without it `signUp.create()` can fail to complete -- which presents as
        a correct code being rejected, because the code genuinely was correct.

        React Native Web maps `nativeID` onto the DOM id, so this is a real
        `<div id="clerk-captcha">` on web and an empty view on native, where
        Clerk handles bot protection differently.
      */}
      <View nativeID="clerk-captcha" style={styles.captcha} />

      {error !== null && <Text style={styles.error}>{error}</Text>}

      <Pressable
        style={[styles.button, (busy || !ready) && styles.buttonDisabled]}
        disabled={busy || !ready}
        onPress={() => void (stage === "email" ? sendCode() : verify())}
      >
        {busy ? (
          <ActivityIndicator color={colour.surface} />
        ) : (
          <Text style={styles.buttonText}>{stage === "email" ? "Continue" : "Sign in"}</Text>
        )}
      </Pressable>

      {stage === "code" && !busy && (
        <Pressable
          onPress={() => {
            setStage("email");
            setCode("");
            setError(null);
          }}
        >
          <Text style={styles.secondary}>Use a different email</Text>
        </Pressable>
      )}
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1, padding: 24, gap: 12, justifyContent: "center" },
  title: { ...type.display, fontWeight: "700", color: colour.ink },
  blurb: { ...type.meta, color: colour.inkMuted, marginBottom: 8 },
  input: {
    backgroundColor: colour.surface,
    borderColor: colour.lineStrong,
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 12,
    ...type.body,
    color: colour.ink,
  },
  code: { ...type.display, letterSpacing: 8, textAlign: "center" },
  error: { color: colour.danger, ...type.meta },
  // Collapses to nothing when Clerk does not need to render anything.
  captcha: { alignItems: "center" },
  google: {
    borderWidth: 1,
    borderColor: colour.lineStrong,
    borderRadius: 8,
    paddingVertical: 12,
    alignItems: "center",
    backgroundColor: colour.surface,
  },
  googleText: { ...type.body, fontWeight: "600", color: colour.ink },
  dividerRow: { flexDirection: "row", alignItems: "center", gap: 12, marginVertical: 4 },
  divider: { flex: 1, height: 1, backgroundColor: colour.line },
  dividerText: { color: colour.inkFaint, ...type.label },
  button: {
    backgroundColor: colour.ink,
    borderRadius: 8,
    paddingVertical: 14,
    alignItems: "center",
    marginTop: 4,
  },
  buttonDisabled: { opacity: 0.6 },
  buttonText: { color: colour.surface, ...type.body, fontWeight: "600" },
  secondary: { color: colour.accent, ...type.meta, textAlign: "center", marginTop: 4 },
});
